import { Tooltip } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks/src/use-resolution-info';
import { InfoIcon } from '@mm/icons';
import ConnectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { CampaignPurpose } from '@mm/state/interfaces/campaignPurposes';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as AlertIcon } from 'assets/icons/alert-action.svg';
import { compact, isNumber, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import RadioInput from '../../../../../common/components/RadioInput';
import { OTN_PURPOSE_ID } from '../../panels/ChatBlasterDetailsPanel/ChatBlasterDetailsPanel';
import { SubscriptionMessagingAlert } from '../SubscriptionMessagingAlert/SubscriptionMessagingAlert';
import { blasterCardTypes } from './blasterCardTypes';
import './BlastTypeSelector.pcss';
import { notificationPurposes as harcodedNotificationPurposes } from './notificationPurposes';

type Props = {
  purpose?: number | string;
  purposeName?: string;
  selectedTagName: string;
  facebookPageId?: number;
  remotePageId?: string;
  notificationPurposes: { id: number; name: string }[];
  setSelectedTagName: (tagName: string) => void;
  onPurposeChange: (purposeId: number, tagName?: string | boolean) => void;
  widgets: WidgetModel[];
};

export const useActivePageMessagingStatus = () => {
  const [messagingApproved, setMessagingApproved] = useState(false);

  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const hasConnectedRealFacebookPage = useSelector(
    hasConnectedRealFacebookPageSelector,
  );
  const facebookPageId = activeFacebookPage?.id;
  const isReal = activeFacebookPage?.realFacebookPage;

  const dispatch: Dispatch<StoreState> = useDispatch();

  useEffect(() => {
    const retrieveMessagingStatus = async () => {
      if (!facebookPageId || !hasConnectedRealFacebookPage || !isReal) return;
      const response = await dispatch(
        ConnectedPageCRUDActions.retrieveSubscriptionMessagingStatus(
          facebookPageId,
        ),
      );

      if (response) setMessagingApproved(response.status === 'approved');
    };
    retrieveMessagingStatus();
  }, [facebookPageId, hasConnectedRealFacebookPage]);

  return {
    isMessagingApproved: messagingApproved,
    remotePageId: activeFacebookPage?.remoteId,
  };
};

// https://github.com/ChatableAI/chatable/blob/staging/app/models/campaign.rb#L41
export const findPurposeId = (
  notificationPurposes: CampaignPurpose[],
  purpose: string | number | undefined,
  purposeName: string | undefined = '',
) => {
  if (isNumber(purpose)) {
    return purpose;
  }

  if (String(purposeName).toLowerCase() === 'otn') {
    return OTN_PURPOSE_ID;
  }

  if (String(purpose).toLowerCase() === 'update') {
    return blasterCardTypes.promoMessage.purposeId;
  }

  if (String(purpose).toLowerCase() === 'non promotional subscription') {
    return blasterCardTypes.subscriptionMessage.purposeId;
  }

  if (notificationPurposes.length && purpose) {
    const selectedPurpose = notificationPurposes.find(
      ({ name }) => name.toLowerCase() === purpose.toLowerCase(),
    );
    if (selectedPurpose) {
      return selectedPurpose.id;
    }
  }
};

export const BlastTypeSelector = ({
  purpose,
  purposeName,
  notificationPurposes,
  onPurposeChange,
  selectedTagName,
  setSelectedTagName,
  widgets,
}: Props) => {
  const purposes: CampaignPurpose[] = [
    ...notificationPurposes,
    ...harcodedNotificationPurposes,
  ];
  const activePurposeId = findPurposeId(purposes, purpose, purposeName);
  const { isMessagingApproved, remotePageId } = useActivePageMessagingStatus();

  const cards = Object.values(blasterCardTypes).filter(({ purposeId }) => {
    return blasterCardTypes.subscriptionMessage.purposeId === purposeId
      ? isMessagingApproved
      : true;
  });
  const selectedCard = Object.values(blasterCardTypes).find(
    ({ purposeId }) => activePurposeId === purposeId,
  );

  const isOtn = activePurposeId === OTN_PURPOSE_ID;
  const selectedNotificationOption = purposes.find(
    ({ id }) => id === activePurposeId,
  );

  useEffect(() => {
    if (isOtn && !selectedTagName) {
      setSelectedTagName(otnTags[0].name);
    }
  }, [isOtn, selectedNotificationOption]);

  const isNotificationSelected = isOtn || !!selectedNotificationOption;

  const onCardClick = (purposeId: number) => {
    if (purposeId === blasterCardTypes.notification.purposeId) {
      // select first option for notification case
      return onPurposeChange(notificationPurposes[0].id);
    }

    onPurposeChange(purposeId);
  };

  const tags = useSelector((state: StoreState) => tagsOfKinds(state, ['otn']));
  const otnTags = tags.map(tag => {
    const name = tag.name ?? '';
    return { name, id: name };
  });

  const selectedNotificationPurposeOTNTag = otnTags.find(
    p => p.id === selectedTagName,
  );

  const notificationTagOptions = [
    {
      id: notificationPurposes[0]?.id,
      name: 'Use a Message Tag',
    },
  ];

  if (otnTags.length > 0) {
    notificationTagOptions.push({
      id: OTN_PURPOSE_ID,
      name: 'Use 1-Time Notification Tag',
    });
  }
  const router = useRouter();
  const { openGeneralConfirmModal } = useModalActions();

  const ri = useResolutionInfo();

  const openOTNWarningmodal = () => {
    openGeneralConfirmModal({
      headerText: 'Warning',
      showHeader: false,
      messageText:
        'For OTN blasts, you must use a Quick Question as your first message.',
      okButtonText: 'Ok',
      onCloseHandler: ok => {
        if (ok) {
          router.history.push(`#dialogue`);
        }
      },
      showCancelButton: false,
    });
  };

  const NotificationUpdateChildren = (
    <>
      <div className="notification-tag-selector">
        {otnTags.length !== 0 && (
          <PrimarySelect
            options={notificationTagOptions}
            value={
              !selectedTagName
                ? notificationTagOptions[0]
                : notificationTagOptions[1]
            }
            onChange={({ id }) => {
              if (id === OTN_PURPOSE_ID) {
                onPurposeChange(id, otnTags[0].name);
                if (
                  widgets &&
                  widgets.length &&
                  widgets[0].type !== WidgetTypes.QUICK_QUESTION
                ) {
                  openOTNWarningmodal();
                }
              } else {
                onPurposeChange(+id);
              }
            }}
          />
        )}
      </div>
      {isOtn ? (
        <div>
          <div>
            Reach contacts who have opted into receiving a 1-time notification
            from your page. Select a 1-time notification tag below that matches
            the content you’re sending.
            {otnTags.length > 0 && (
              <span
                className="notification-type__notification-tag--link"
                onClick={() => {
                  window.open(
                    'https://customers.ai/help/article/8xwzn7qmpe-1-time-notification-widget',
                  );
                }}
              >
                <i className="far fa-question-circle" />
              </span>
            )}
          </div>

          <div className="blast-type-selector__purpose-dropdown">
            <PrimarySelect
              value={selectedNotificationPurposeOTNTag || otnTags[0]}
              options={otnTags}
              onChange={tag => setSelectedTagName(tag.name || '')}
            />
          </div>
          <div className="blast-type-selector__tag-fb-guideline-wrapper">
            <AlertIcon className="blast-type-selector__tag-alert-icon" />
            1-Time Notification Message must meet
            <a
              className="blast-type-selector__tag-fb-guideline-link"
              target="_blank"
              rel="noopener noreferrer"
              href="https://customers.ai/help/article/lbfdv33b68-facebook-s-policy-guidelines-for-one-time-notifications-otn"
            >
              FB's Guidelines
            </a>
          </div>
        </div>
      ) : (
        <div>
          <div>
            Reach for free any contacts who have opted into receiving
            notifications or updates from your page. You must select a
            <a
              className="notification-message-tag-link"
              href="https://developers.facebook.com/docs/messenger-platform/send-messages/message-tags/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Message tag
            </a>
            below that most closely matches the type of content you are sending.
          </div>
          <div className="blast-type-selector__purpose-dropdown">
            <PrimarySelect
              value={selectedNotificationOption || notificationPurposes[0]}
              options={uniqBy(
                compact([...notificationPurposes, selectedNotificationOption]),
                'id',
              )}
              onChange={({ id }) => onPurposeChange(Number(id))}
            />
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="blast-type-selector">
      <div className="blast-type-selector__cards-block">
        {cards.map(({ purposeId, name, info, type }) => {
          const isActive =
            activePurposeId === purposeId ||
            (isNotificationSelected &&
              purposeId === blasterCardTypes.notification.purposeId);
          return (
            <RadioInput
              className="blast-type-selector__input"
              key={purposeId}
              name={type}
              checked={isActive}
              value={type}
              label={
                <span className="blast-type-selector__input-label">{name}</span>
              }
              onChange={() => onCardClick(purposeId)}
            >
              {info && (
                <Tooltip
                  title={info}
                  placement={ri.screenWidth < 1500 ? 'right' : 'bottom'}
                >
                  <InfoIcon />
                </Tooltip>
              )}
            </RadioInput>
          );
        })}
      </div>
      <div>
        {blasterCardTypes.subscriptionMessage.purposeId === activePurposeId &&
        !isMessagingApproved ? (
          <div className="blast-type-selector__error-container">
            <SubscriptionMessagingAlert remotePageId={remotePageId} />
          </div>
        ) : (
          <div className="blast-type-selector__children-container">
            {isNotificationSelected && NotificationUpdateChildren}
          </div>
        )}
      </div>
    </div>
  );
};
